/**
 * The voices of one staff: which line of the staff each note is written in.
 *
 * A track holds a note down while it plays others, and a single written voice shows one
 * thing at a time. So a staff that sounds two lines at once is written in two voices, the
 * two a staff is read in: stems up and stems down. A second line is opened only when the
 * first is still holding something, and where two are going the next note goes to the one
 * that has let go nearest to it in pitch.
 *
 * A staff is not written in a third. What is struck while both voices are holding goes into
 * the one sounding nearest it, and the notes that voice is holding are tied through it
 * ({@link spansOf}): the held note is written again under the new one, joined by a tie, as
 * a chord with one note held over is written by hand.
 *
 * Two things that look like a second line are not one, in a file that was played. Notes
 * struck together and let go within a grid step of each other are a chord. And a note let
 * go a grid step after the next one is struck is legato, a hand not yet off the key, and is
 * cut where the next one starts rather than given a voice to finish in. A note let go
 * exactly on the grid was written that long, and is left that long.
 *
 * The lines are counted a stretch at a time, a stretch being the bars no note is held
 * across the ends of. Each stretch starts with none, so a bar that sounds one line is
 * written in one voice whatever the bar before it needed, and within a stretch the two are
 * numbered from the higher down, which is how voices are numbered on a staff. A tied note
 * therefore keeps its voice across the bar line.
 */

import { PPQ } from "../document/duration";
import { barAt } from "./spans";
import type { BarSpan, ScaledNote } from "./spans";

/** How many voices a staff is written in at the most: stems up and stems down. */
const MOST = 2;
/** What a beat of silence in a line costs, against a semitone of leap. */
const SILENCE = 1;
/** What cutting a legato note short costs, against a semitone of leap. */
const CUT = 2;

/** Notes struck together and let go together: one event of one voice. */
interface Chord {
  readonly start: number;
  ticks: number;
  readonly keys: number[];
  /** How much of its length is not taken at its word: a grid step where it was played. */
  slack: number;
}

/** One line of a stretch: its chords in the order struck, and those still sounding. */
interface Line {
  readonly chords: Chord[];
  sounding: Chord[];
}

function middle(chords: readonly Chord[]): number {
  const keys = chords.flatMap((chord) => chord.keys);
  return keys.reduce((sum, key) => sum + key, 0) / keys.length;
}

/**
 * The chords struck at each attack, longest first, one for each length a grid step apart. Of
 * one key struck twice at an attack the longer is kept, as a keyboard sounds it.
 */
function chordsOf(notes: readonly ScaledNote[], unit: number): Chord[][] {
  const struck = new Map<number, ScaledNote[]>();
  for (const note of notes) struck.set(note.start, [...(struck.get(note.start) ?? []), note]);

  return [...struck.keys()]
    .sort((first, second) => first - second)
    .map((start) => {
      const chords: Chord[] = [];
      const keys = new Set<number>();
      const longest = [...(struck.get(start) ?? [])].sort((a, b) => b.ticks - a.ticks);
      for (const note of longest) {
        if (keys.has(note.key)) continue;
        keys.add(note.key);
        const last = chords[chords.length - 1];
        const slack = note.played === true ? unit : 0;
        if (last !== undefined && last.ticks - note.ticks <= Math.max(slack, last.slack)) {
          last.keys.push(note.key);
          last.slack = Math.max(slack, last.slack);
        } else chords.push({ start, ticks: Math.max(unit, note.ticks), keys: [note.key], slack });
      }
      return chords;
    });
}

/** The chords of a line still held when `chord` is struck: sounding into it, past any slack. */
function held(line: Line, chord: Chord): Chord[] {
  return line.sounding.filter((other) => other.start + other.ticks - chord.start > other.slack);
}

/** What giving `chord` to a line that has let go costs: its leap, and the silence or the cut. */
function following(line: Line, chord: Chord): number {
  const last = line.chords[line.chords.length - 1] as Chord;
  const over = last.start + last.ticks - chord.start;
  const leap = Math.abs(middle([chord]) - middle([last]));
  return over > 0 ? leap + CUT : leap + (-over / PPQ) * SILENCE;
}

/** Add `chord` to a line: what it was only just holding is cut there, the rest is tied on. */
function give(line: Line, chord: Chord): void {
  const kept = held(line, chord);
  for (const other of line.sounding) {
    if (!kept.includes(other)) other.ticks = Math.min(other.ticks, chord.start - other.start);
  }
  line.chords.push(chord);
  line.sounding = [...kept, chord];
}

/** Give the chords of one attack to the lines going, opening one where all are holding. */
function place(attack: readonly Chord[], lines: Line[]): void {
  const waiting = [...attack];
  while (waiting.length > 0) {
    let least = Infinity;
    let chosen: { chord: number; line: Line } | null = null;
    for (const [at, chord] of waiting.entries()) {
      for (const line of lines) {
        if (held(line, chord).length > 0) continue;
        const cost = following(line, chord);
        if (cost < least) {
          least = cost;
          chosen = { chord: at, line };
        }
      }
    }
    if (chosen === null && lines.length < MOST) {
      const opened: Line = { chords: [], sounding: [] };
      lines.push(opened);
      chosen = { chord: 0, line: opened };
    }
    // Every line is holding and there is no room for another: tied into the nearest.
    if (chosen === null) {
      for (const [at, chord] of waiting.entries()) {
        for (const line of lines) {
          const cost = Math.abs(middle([chord]) - middle(held(line, chord)));
          if (cost < least) {
            least = cost;
            chosen = { chord: at, line };
          }
        }
      }
    }
    if (chosen === null) return;
    give(chosen.line, waiting.splice(chosen.chord, 1)[0] as Chord);
  }
}

/** The lines of one stretch, the higher first, each added to the voice of its number. */
function number(lines: readonly Line[], voices: ScaledNote[][]): void {
  const ranked = [...lines].sort((first, second) => middle(second.chords) - middle(first.chords));
  ranked.forEach((line, voice) => {
    const into = voices[voice] ?? [];
    for (const chord of line.chords) {
      for (const key of chord.keys) into.push({ key, start: chord.start, ticks: chord.ticks });
    }
    voices[voice] = into;
  });
}

/**
 * One staff's notes as voices, the higher line first. A voice's notes may sound over one
 * another, and are tied where they do; nothing is left out and nothing is shortened but
 * what legato overlaps.
 */
export function voicesOf(
  notes: readonly ScaledNote[],
  bars: readonly BarSpan[],
  unit: number,
): readonly (readonly ScaledNote[])[] {
  const voices: ScaledNote[][] = [[]];
  let lines: Line[] = [];
  for (const attack of chordsOf(notes, unit)) {
    const at = (attack[0] as Chord).start;
    const barLine = (bars[barAt(bars, at)] as BarSpan).start;
    // Nothing is held over the bar line this attack follows: a new stretch, from no lines.
    const letGo = lines.every((line) =>
      line.sounding.every((chord) => chord.start + chord.ticks <= barLine),
    );
    if (letGo) {
      number(lines, voices);
      lines = [];
    }
    for (const line of lines) {
      line.sounding = line.sounding.filter((chord) => chord.start + chord.ticks > at);
    }
    place(attack, lines);
  }
  number(lines, voices);
  return voices;
}
